import { useEffect, useRef } from 'react';

import { useTraceDrawerStore } from '@/sections/insights/aiAssistant/stores/traceDrawer.store';
import { TraceEvent } from '@/types';

import { useWorkflowStatus } from './useWorkflowStatus';

interface UseTraceDrawerAutoOpenOptions {
  /** The trace ID for the current conversation */
  traceId: string | undefined;
  /** The trace events */
  traces: TraceEvent[] | undefined;
  /** Whether auto-open is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Automatically opens the trace drawer when a workflow starts.
 * Only opens once per workflow start (not on every trace update).
 */
export function useTraceDrawerAutoOpen({
  traceId,
  traces,
  enabled = true,
}: UseTraceDrawerAutoOpenOptions): void {
  const { open, isOpen } = useTraceDrawerStore();
  const { isRunning } = useWorkflowStatus(traces);
  const previousTraceIdRef = useRef<string | undefined>(undefined);
  const hasAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !traceId) {
      return;
    }

    // Reset auto-open flag when traceId changes (new conversation)
    if (traceId !== previousTraceIdRef.current) {
      hasAutoOpenedRef.current = false;
      previousTraceIdRef.current = traceId;
    }

    // Auto-open when workflow starts running and hasn't been auto-opened yet
    if (isRunning && !isOpen && !hasAutoOpenedRef.current) {
      hasAutoOpenedRef.current = true;
      open();
    }
  }, [traceId, isRunning, isOpen, enabled, open]);
}
